import React, { FC } from 'react'

import { ContentfulProcessor, Typography } from '@smarty-nx/ds'
import cn from 'classnames'

import FaqItem from './FaqItem'
import { faqSectionContent } from './FaqSection.content'
import classes from './FaqSection.module.scss'
import paddingClasses from '../../components/styles/padding.module.scss'
import spacerClasses from '../../components/styles/spacer.module.scss'
import TextLink from '../../components/TextLink'
import { isBanner, isFaq } from '../../node_pages/content-type-checks'
import FaqBanner from '../FaqBanner'

export interface FaqSectionProps extends Queries.FaqSectionFragment {
  isReferAFriend?: boolean
  darkText?: boolean
}

const FaqSection: FC<FaqSectionProps> = ({
  header,
  items,
  link,
  darkText,
  isReferAFriend,
}) => {
  if (!items) {
    return null
  }

  const faqAccordions = items.filter(isFaq)
  const faqBanners = items.filter(isBanner)

  /* half the faq content array (using ceil to round up) and assign to left and right columns */
  const leftAccordions = faqAccordions.slice(
    0,
    Math.ceil(faqAccordions.length / 2)
  )
  const rightAccordions = faqAccordions.slice(
    Math.ceil(faqAccordions.length / 2),
    faqAccordions.length
  )

  return (
    <>
      {isReferAFriend ? (
        <>
          <Typography
            component="h2"
            variant="Kollektif/ExtraLarge"
            className={cn(
              classes.faqTitle,
              classes.alignCenter,
              spacerClasses.spacerTop
            )}
          >
            {header}
          </Typography>
          <Typography variant="Kollektif/Small" className={classes.centered}>
            {faqSectionContent.onlyReferFriendFAQs}{' '}
            <TextLink to="/#faq">
              {faqSectionContent.viewFAQsOnNetwork}
            </TextLink>
          </Typography>
        </>
      ) : (
        <Typography
          component="h2"
          variant="Kollektif/ExtraLarge"
          className={classes.faqTitle}
          data-testid={faqSectionContent.faqTitleId}
        >
          {header}
        </Typography>
      )}
      <section
        className={cn(classes.faqContainer, paddingClasses.paddingBottomSm)}
        data-module-name="faq"
        data-testid="faqs-section"
      >
        <div className={classes.faqColumn}>
          {leftAccordions.map((faq) => (
            <FaqItem {...faq} darkText={darkText} key={faq.faqId} />
          ))}
        </div>
        <div className={classes.faqColumn}>
          {rightAccordions.map((faq) => (
            <FaqItem {...faq} darkText={darkText} key={faq.faqId} />
          ))}
        </div>
      </section>

      {link && (
        <Typography
          variant="Kollektif/Small"
          className={cn(isReferAFriend && classes.centered)}
        >
          <ContentfulProcessor
            content={link.internal.content || ''}
            components={{
              a({ node: _, children, ...props }) {
                return <TextLink {...props}>{children}</TextLink>
              },
            }}
          />
        </Typography>
      )}

      {faqBanners.length > 0 && (
        <div className={cn(classes.banners, spacerClasses.spacer2x)}>
          {faqBanners.map((banner) => (
            <FaqBanner banner={banner} key={banner.type} />
          ))}
        </div>
      )}
    </>
  )
}

export default FaqSection
